import { IconButton, Modal, SingleSelectDropdown, Text } from "@carbon6/c6-frontend-design";
import { useEffect, useState } from "react";
import { listAllEmbodiments, type Embodiment } from "@/api";
import { hatcheryDropdownModes, hatcheryTextModes } from "@/app/theme";
import { HatModalHeader } from "@/components/modal";
import { EmbodimentDetailContent } from "./EmbodimentDetailContent";
import { formatEmbodimentMeta } from "./embodimentDisplay";
import { resolveEmbodimentErrorMessage } from "./embodimentErrors";
import type { EmbodimentComposerSource } from "./EmbodimentComposerModal";
import styles from "./EmbodimentSourceChoiceDialog.module.css";

export type EmbodimentSourceChoiceDialogProps = {
  open: boolean;
  onClose: () => void;
  // 선택 결과다: 빈 폼(blank) 또는 기존 embodiment 복제(clone).
  onChoose: (source: EmbodimentComposerSource) => void;
};

// '새 Embodiment' 진입 시 먼저 표시하는 선택 창이다. 빈 폼에서 새로 작성할지, 기존 embodiment 를
// 복제해 새로 생성할지 고른다. '기존에서 가져오기'는 이 창에서 원본을 고르면 상세를 미리 보여준다.
// Footer 의 [생성] 을 눌러야 실제로 진행한다.
export function EmbodimentSourceChoiceDialog({
  open,
  onClose,
  onChoose,
}: EmbodimentSourceChoiceDialogProps) {
  const [embodiments, setEmbodiments] = useState<readonly Embodiment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pickExisting, setPickExisting] = useState(false);
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setPickExisting(false);
      setSelectedUuid(null);
      setError(null);
      return;
    }
    let alive = true;
    setLoading(true);
    listAllEmbodiments()
      .then((list) => alive && setEmbodiments(list))
      .catch((caught) => alive && setError(resolveEmbodimentErrorMessage(caught)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [open]);

  const selected =
    selectedUuid !== null
      ? (embodiments.find((entry) => entry.uuid === selectedUuid) ?? null)
      : null;

  return (
    <Modal open={open} onClose={onClose} size="md" aria-labelledby="embodiment-source-choice-title">
      <HatModalHeader
        title="새 Embodiment를 만듭니다."
        titleId="embodiment-source-choice-title"
        close
      />
      <div className={styles.body}>
        <div className={styles.options}>
          <button
            type="button"
            className={styles.option}
            onClick={() => onChoose({ kind: "blank" })}
          >
            <Text variant={hatcheryTextModes["body-bold"]}>새로 작성</Text>
            <Text variant={hatcheryTextModes["caption-normal"]} className={styles.optionHint}>
              빈 폼에서 처음부터 구성합니다.
            </Text>
          </button>
          <button
            type="button"
            className={pickExisting ? styles.optionActive : styles.option}
            aria-pressed={pickExisting}
            onClick={() => setPickExisting(true)}
          >
            <Text variant={hatcheryTextModes["body-bold"]}>기존에서 가져오기</Text>
            <Text variant={hatcheryTextModes["caption-normal"]} className={styles.optionHint}>
              기존 embodiment 정보를 토대로 새로 생성합니다.
            </Text>
          </button>
        </div>
        {pickExisting ? (
          <SingleSelectDropdown
            mode={hatcheryDropdownModes.default}
            label="기존 embodiment"
            value={selectedUuid}
            options={embodiments.map((entry) => ({
              value: entry.uuid,
              label: entry.name,
              // 보조 줄이다. arms · EE(name:tag) · 카메라 수로 복제할 embodiment 를 식별한다.
              description: formatEmbodimentMeta(entry),
            }))}
            placeholder={
              loading ? "Embodiment 목록을 불러오고 있습니다." : "Embodiment를 선택해 주세요."
            }
            searchable
            disabled={loading}
            onValueChange={(value) => setSelectedUuid(value)}
          />
        ) : null}
        {selected ? (
          <div className={styles.preview}>
            <EmbodimentDetailContent embodiment={selected} flush />
          </div>
        ) : null}
        {error ? (
          <Text variant={hatcheryTextModes["caption-normal"]} className={styles.error} role="alert">
            {error}
          </Text>
        ) : null}
      </div>
      <Modal.Footer>
        <IconButton text="취소" variant="surface" onClick={onClose} />
        {pickExisting ? (
          <IconButton
            text="생성"
            variant="solid"
            tone="primary"
            disabled={selectedUuid === null}
            onClick={() =>
              selectedUuid !== null && onChoose({ kind: "clone", embodimentUuid: selectedUuid })
            }
          />
        ) : null}
      </Modal.Footer>
    </Modal>
  );
}
